import Axios from "functions/Axios";
import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useId,
} from "react";

/**
 * PropertiesPopup
 * ----------------
 * A "Properties" row styled like a context-menu item (icon + label, full
 * width, hover highlight) that opens a flyout popup to the RIGHT of the
 * row on click — matching the "Label By Color" -> "Folder Color" flyout
 * pattern.
 *
 * API contract (GET {baseUrl}/apidam/get-folder-size?folderIds={folderId}):
 * {
 *   "data": {
 *     "fileSize": 334.95,              // assumed MB
 *     "fileTypes": [["video", 3], ["image", 1]]
 *   },
 *   "success": true,
 *   "message": "Available folder size"
 * }
 *
 * Usage (drop straight into an existing context-menu list):
 *   <ul className="my-context-menu">
 *     <li>...Label By Color...</li>
 *     <li>...Share...</li>
 *     <li><PropertiesPopup folderId={folder.id} /></li>
 *   </ul>
 */

const DEFAULT_BASE_URL =
  (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_DAM_URL) ||
  "{{dam}}";

// ---- helpers ---------------------------------------------------------

function formatFileSize(sizeInMb) {
  if (sizeInMb == null || Number.isNaN(sizeInMb)) return "—";
  if (sizeInMb >= 1024) return `${(sizeInMb / 1024).toFixed(2)} GB`;
  if (sizeInMb < 1) return `${(sizeInMb * 1024).toFixed(0)} KB`;
  return `${sizeInMb.toFixed(2)} MB`;
}

const TYPE_ICONS = {
  video: (
    <path d="M4 5h11a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Zm13 4 5-3v12l-5-3" />
  ),
  image: (
    <>
      <rect x="2.5" y="4" width="16" height="14" rx="2" />
      <circle cx="7.5" cy="9" r="1.5" />
      <path d="m4 16 4.5-5 3.5 4 2.5-3 4 4" />
    </>
  ),
  document: (
    <>
      <path d="M6 2.5h6l4 4v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-16a1 1 0 0 1 1-1Z" />
      <path d="M12 2.5v4h4" />
    </>
  ),
  audio: (
    <>
      <path d="M7 15V5l10-2v10" />
      <circle cx="5" cy="16" r="2.5" />
      <circle cx="15" cy="13.5" r="2.5" />
    </>
  ),
  archive: (
    <>
      <rect x="3" y="4" width="14" height="14" rx="1" />
      <path d="M8 4v14M8 7h2M8 11h2" />
    </>
  ),
  other: <path d="M4 4h12v12H4z" />,
};

function TypeIcon({ type }) {
  const path = TYPE_ICONS[type?.toLowerCase()] || TYPE_ICONS.other;
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {path}
    </svg>
  );
}

function capitalize(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ---- component ---------------------------------------------------------

export default function PropertiesPopup({
  folderId,
  baseUrl = DEFAULT_BASE_URL,
  label = "Properties",
  className = "",
  rowClassName = "",
}) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [direction, setDirection] = useState("right"); // 'right' | 'left'

  const wrapperRef = useRef(null);
  const rowRef = useRef(null);
  const popoverRef = useRef(null);
  const popoverId = useId();

  const fetchProperties = useCallback(async () => {
    if (!folderId) {
      setStatus("error");
      setErrorMsg("No folder selected.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await Axios.get(
        `${baseUrl}/apidam/folder/folder-property?folderIds=${encodeURIComponent(
          folderId
        )}`
      );

      setResult(res.data.data);
      setStatus("success");
    } catch (err) {
      setErrorMsg(err.message || "Couldn't load properties.");
      setStatus("error");
    }
  }, [folderId, baseUrl]);

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) fetchProperties();
      return next;
    });
  };

  // flip to the left side if there isn't enough room on the right,
  // so the popover is always fully visible on screen
  useLayoutEffect(() => {
    if (!open) return;

    function updateDirection() {
      const wrapperEl = wrapperRef.current;
      const popoverEl = popoverRef.current;
      if (!wrapperEl) return;

      const wrapperRect = wrapperEl.getBoundingClientRect();
      const popoverWidth = popoverEl?.offsetWidth || 250;
      const gap = 6;
      const edgePadding = 12;

      const spaceOnRight = window.innerWidth - wrapperRect.right;
      const spaceOnLeft = wrapperRect.left;

      if (spaceOnRight < popoverWidth + gap + edgePadding && spaceOnLeft > spaceOnRight) {
        setDirection("left");
      } else {
        setDirection("right");
      }
    }

    updateDirection();
    window.addEventListener("resize", updateDirection);
    return () => window.removeEventListener("resize", updateDirection);
  }, [open]);

  // close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function handleKey(e) {
      if (e.key === "Escape") {
        setOpen(false);
        rowRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const totalFiles =
    result?.fileTypes?.reduce((sum, [, count]) => sum + count, 0) ?? 0;

  return (
    <span className={`pp-wrapper ${className}`} ref={wrapperRef}>
      <button
        type="button"
        ref={rowRef}
        className={`pp-row ${open ? "pp-row-active" : ""} ${rowClassName}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={handleToggle}
      >
        <svg
          className="pp-row-icon"
          width="16"
          height="16"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <circle cx="10" cy="10" r="7.25" />
          <path d="M10 8.5v4.25M10 6.75v.01" strokeLinecap="round" />
        </svg>
        <span className="pp-row-label">{label}</span>
      </button>

      {open && (
        <div
          ref={popoverRef}
          id={popoverId}
          role="dialog"
          aria-label="Folder properties"
          className={`pp-popover pp-popover-${direction}`}
        >
          <p className="pp-popover-title">Properties</p>

          {status === "loading" && (
            <div className="pp-state">
              <span className="pp-spinner" aria-hidden="true" />
              Loading properties…
            </div>
          )}

          {status === "error" && (
            <div className="pp-state pp-error">
              {errorMsg}
              <button className="pp-retry" onClick={fetchProperties}>
                Retry
              </button>
            </div>
          )}

          {status === "success" && result && (
            <div className="pp-content">
              <div className="pp-info-row">
                <span className="pp-info-label">Total size</span>
                <span className="pp-info-value pp-size">
                  {formatFileSize(result.fileSize)}
                </span>
              </div>

              <div className="pp-divider" />

              <div className="pp-info-row">
                <span className="pp-info-label">Files</span>
                <span className="pp-info-value">{totalFiles}</span>
              </div>

              {result.fileTypes?.length > 0 && (
                <ul className="pp-type-list">
                  {result.fileTypes.map(([type, count]) => (
                    <li key={type} className="pp-type-row">
                      <span className="pp-type-label">
                        <TypeIcon type={type} />
                        {capitalize(type)}
                      </span>
                      <span className="pp-type-count">{count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      <style>{`
        .pp-wrapper {
          position: relative;
          display: block;
        }

        /* --- menu-item trigger, matches the reference list style --- */
        .pp-row {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 10px 14px;
          font-size: 14px;
          font-weight: 400;
          text-align: left;
          color: #2c2c2a;
          background: transparent;
          border: none;
          cursor: pointer;
        }
        .pp-row:hover,
        .pp-row-active {
          background: #eeece6;
        }
        .pp-row-icon {
          flex-shrink: 0;
          color: #4a4a47;
        }
        .pp-row-label {
          flex: 1;
        }

        /* --- flyout popover, opens to the right of the row --- */
        .pp-popover {
          position: absolute;
          top: 0;
          z-index: 50;
          width: 250px;
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 12px 28px rgba(20, 20, 18, 0.16), 0 2px 8px rgba(20, 20, 18, 0.08);
          padding: 14px 16px;
          font-size: 13px;
          color: #2c2c2a;
          animation: pp-fade-in 0.12s ease-out;
        }
        .pp-popover-right {
          left: calc(100% + 6px);
        }
        .pp-popover-left {
          right: calc(100% + 6px);
        }
        @keyframes pp-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .pp-popover-title {
          margin: 0 0 10px;
          font-size: 13px;
          font-weight: 500;
          color: #a97a45;
        }
        .pp-state {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #6b6a64;
          padding: 4px 0;
        }
        .pp-error {
          flex-direction: column;
          align-items: flex-start;
          color: #a32d2d;
          gap: 8px;
        }
        .pp-retry {
          font-size: 12px;
          font-weight: 500;
          color: #2c2c2a;
          background: #f1efe8;
          border: none;
          border-radius: 6px;
          padding: 4px 10px;
          cursor: pointer;
        }
        .pp-spinner {
          width: 13px;
          height: 13px;
          border: 2px solid #e0ded8;
          border-top-color: #6b6a64;
          border-radius: 50%;
          animation: pp-spin 0.6s linear infinite;
          flex-shrink: 0;
        }
        @keyframes pp-spin {
          to { transform: rotate(360deg); }
        }
        .pp-info-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 2px 0;
        }
        .pp-info-label {
          color: #6b6a64;
        }
        .pp-info-value {
          font-weight: 500;
        }
        .pp-size {
          font-variant-numeric: tabular-nums;
        }
        .pp-divider {
          height: 1px;
          background: #ece9e2;
          margin: 8px 0;
        }
        .pp-type-list {
          list-style: none;
          margin: 8px 0 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .pp-type-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .pp-type-label {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #3a3a38;
        }
        .pp-type-count {
          font-weight: 500;
          font-variant-numeric: tabular-nums;
          color: #6b6a64;
        }
      `}</style>
    </span>
  );
}